import React, { useState, useEffect } from 'react';
import './SavedFiltersDataTable.scss';
import { Search, Eye } from '@livingdesign/icons';
import { 
  TextField, 
  Button,
  Checkbox,
  IconButton,
  Skeleton
} from '@livingdesign/react';
import Datatable from '../Datatable/Datatable';
import Pagination from '../Pagination/Pagination';
import DataTableBulkActions from '../DataTableBulkActions/DataTableBulkActions';
import SavedFiltersOptionsMenu from './SavedFiltersOptionsMenu';
import { useAppContext } from '../../../../contexts/Core';
import { formatDateShort } from '../../../../utils/common';
import { ACTIONS } from '../../../../consts/CommonV2';
import { useSavedFiltersManagement } from '../../../../contexts/SavedFiltersManagement';


export const QA_ID_PREFIX = 'SavedFiltersDataTable';

function SavedFiltersDataTable({ 
  // Configuration props
  mode,
  searchTerm = '',
  onSearchChange,
  selectedFilter = null,
  onSelectionChange,
  onApply,
  onMoreActions,
  // Action callbacks for management mode
  onView,
  onEdit,
  deleteFilter,
  // Customization props
  searchPlaceholder = "Search for contact lists",
  className = '',
  qaPrefix = QA_ID_PREFIX,
  showSearch = true,
  showBulkActions = true,
  itemsPerPageDefault = 8,
  // Create button props
  showCreateButton = false,
  createButtonText = "Create",
  createButtonIcon = null,
  onCreateClick
}) {
  const {  
    savedFiltersError, 
  } = useAppContext();

  const {
    savedFilters,
    savedFiltersLoading,
    fetchRecipientFiltersListForTeam,
  } = useSavedFiltersManagement();
  
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageDefault);
  const [itemOffset, setItemOffset] = useState(0);

  // Use external search term if provided, otherwise use local
  const activeSearchTerm = onSearchChange ? searchTerm : localSearchTerm;

  // Pagination calculations
  const endOffset = itemOffset + parseInt(itemsPerPage);
  const currentItems = filteredData.slice(itemOffset, endOffset);
  const totalItems = filteredData.length;

  // Initialize filtered data when savedFilters change
  useEffect(() => {
    setFilteredData(savedFilters || []);
  }, [savedFilters]);

  // Filter data based on search term with debouncing
  useEffect(() => {
    if (!savedFilters) return;
    
    const debounceTimer = setTimeout(() => {
      const filtered = savedFilters.filter(filter =>
        filter?.name.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
        filter?.createdBy.toLowerCase().includes(activeSearchTerm.toLowerCase())
      );
      setFilteredData(filtered);
      setCurrentPage(0);
      setItemOffset(0);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [activeSearchTerm, savedFilters]);

  // Load data on mount if not already loaded
  useEffect(() => {
    if (!savedFilters && !savedFiltersLoading && !savedFiltersError) {
      fetchRecipientFiltersListForTeam();
    }
  }, [savedFilters, savedFiltersLoading, savedFiltersError, fetchRecipientFiltersListForTeam]);

  const handleLocalSearchChange = (event) => {
    if (onSearchChange) {
      onSearchChange(event);
    } else {
      setLocalSearchTerm(event.target.value);
    }
  };

  const handleClearSearch = () => {
    if (onSearchChange) {
      onSearchChange({ target: { value: '' } });
    } else {
      setLocalSearchTerm('');
    }
  };

  const handleItemSelect = (itemId) => {
    if (onSelectionChange) {
      const newSelectionFilterId = selectedFilter === itemId ? null : itemId;
      const selectedFilterData = savedFilters.find(filter => filter.filterId === newSelectionFilterId);
      onSelectionChange({newSelectionFilterId, selectedFilterData});
    }
  };

  const handleViewClick = (item) => {
    console.log('View clicked for:', item);
    if (onView) {
      onView(item);
    }
  };

  const handleClearAllSelections = () => {
    if (onSelectionChange) {
      onSelectionChange({});
    }
  };

  const handleApplyFilters = () => {
    if (selectedFilter && onApply) {
      const selectedFilterData = savedFilters.find(filter => filter.filterId === selectedFilter);
      onApply(selectedFilterData);
    }
  };

  const formatRecipientCount = (count) => {
    if(!count && count !== 0) return 'N/A';
    if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'k';
    }
    return count.toLocaleString();
  };

  // Row rendering helper functions
  const renderCheckboxCell = (item) => (
    <Checkbox
      checked={selectedFilter === item.filterId}
      onChange={() => handleItemSelect(item.filterId)}
      id={`${qaPrefix}-row-${item.id}-checkbox`}
      a11yLabelledBy={`${qaPrefix}-row-${item.id}-name`}
      size="small"
      data-testid={`${qaPrefix}-row-${item.id}-checkbox`}
    />
  );

  const renderRecipientListCell = (item) => (
    <div 
      className="saved-filters-cell-content" 
      data-testid={`${qaPrefix}-row-${item.id}-content`}
    >
      <div 
        className="saved-filters-item-name" 
        data-testid={`${qaPrefix}-row-${item.id}-name`}
      >
        {item.name}
      </div>
      <div 
        className="saved-filters-item-meta" 
        data-testid={`${qaPrefix}-row-${item.id}-meta`}
      >
        <span 
          className="saved-filters-author" 
          data-testid={`${qaPrefix}-row-${item.id}-author`}
        >
          {item?.createdBy}
        </span>
        {mode !== ACTIONS.CAMPAIGN_PAGE && (
          <span 
            className="saved-filters-date" 
            data-testid={`${qaPrefix}-row-${item.id}-date`}
          >
            Created on {item.createdDate}
          </span>
        )}
      </div>
    </div>
  );

  const renderLastRefreshedCell = (item) => {
    const timestamp = item.lastUsed;
    const formattedDate = timestamp ? formatDateShort(timestamp) : 'N/A';
    
    return (
      <span className="saved-filters-refresh-date" data-testid={`${qaPrefix}-row-${item.id}-last-refreshed`}>
        {formattedDate}
      </span>
    );
  };

  const renderRecipientCountCell = (item) => (
    <span 
      className="saved-filters-recipient-count"
      data-testid={`${qaPrefix}-row-${item.id}-count`}
    >
      {formatRecipientCount(item?.recipientCount)}
    </span>
  );

  const renderSelectionActionCell = (item) => (
    <Button
      variant="secondary"
      size="small"
      disabled={true}
      onClick={() => {}}
      data-testid={`${qaPrefix}-row-${item.id}-view-button`}
    >
      <Eye />
      <span>View</span>
    </Button>
  );

  const renderManagementActionCell = (item) => (
    <SavedFiltersOptionsMenu
      filter={item}
      id={`${qaPrefix}-row-${item.id}`}
      onView={()=> handleViewClick(item)}
      onEdit={() => {}}
      onDuplicate={() => {}}
      deleteFilter={deleteFilter}
    />
  );

  // Create table row for a single item based on mode
  const createTableRow = (item) => {
    console.log({item});
    
    //campaign page
    if (mode === ACTIONS.CAMPAIGN_PAGE) {
      return [
        renderCheckboxCell(item),
        renderRecipientListCell(item),
        renderRecipientCountCell(item),
        renderSelectionActionCell(item)
      ];
    } else { // recipient page
      return [
        renderRecipientListCell(item),
        renderLastRefreshedCell(item),
        renderRecipientCountCell(item),
        renderManagementActionCell(item)
      ];
    }
  };

  // Prepare table data for common Datatable component based on mode
  const tableHeaders = mode === ACTIONS.CAMPAIGN_PAGE 
    ? [
        { label: '', key: 'select' },
        { label: 'Recipient list', key: 'name' },
        { label: 'Recipients', key: 'count' },
        { label: 'Action', key: 'action' }
      ]
    : [
        { label: 'Recipient list', key: 'name' },
        { label: 'Last refreshed', key: 'lastRefreshed' },
        { label: 'Recipients (approx.)', key: 'recipientCount' },
        { label: 'Actions', key: 'actions' }
      ];

  const tableRows = currentItems.map(createTableRow);

  return (
    <div className={`saved-filters-data-table-container ${className}`} data-testid={`${qaPrefix}-wrapper`}>
      {/* Search Field */}
      {showSearch && (
        <div className="saved-filters-search-section" data-testid={`${qaPrefix}-search-section`}>
          <div className="saved-filters-search-container" data-testid={`${qaPrefix}-search-container`}>
            <TextField
              label=""
              textFieldProps={{
                placeholder: searchPlaceholder
             }}
              value={activeSearchTerm}
              onChange={handleLocalSearchChange}
              leadingIcon={<Search />}
              trailing={
                activeSearchTerm ? (
                  <IconButton a11yLabel="Clear search" onClick={handleClearSearch} data-testid={`${qaPrefix}-clear-search-button`}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </IconButton>
                ) : undefined
              }
              UNSAFE_className="saved-filters-search-field"
              disabled={savedFiltersLoading || !!savedFiltersError}
              data-testid={`${qaPrefix}-search-field`}
            />
          </div>
          {/* Create Button */}
          {showCreateButton && (
            <div className="saved-filters-create-button-container" data-testid={`${qaPrefix}-create-button-container`}>
              <Button
                variant="primary"
                size="medium"
                leading={createButtonIcon}
                onClick={onCreateClick}
                data-testid={`${qaPrefix}-create-button`}
              >
                {createButtonText}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Data Table Container */}
      <div className="saved-filters-table-container" data-testid={`${qaPrefix}-table-container`}>
        {savedFiltersLoading ? (
          <Skeleton height={400} variant="rectangle" color='#7f7f7fff' data-testid={`${qaPrefix}-loading-skeleton`} />
        ) : (
          <>
            {/* Bulk Actions - only show when in selection mode and item is selected */}
            {mode === ACTIONS.CAMPAIGN_PAGE && selectedFilter && showBulkActions && (
              <DataTableBulkActions
                selectedCount={1}
                onSelectAll={null} // Remove select all for single selection
                onClearSelection={handleClearAllSelections}
                onApply={handleApplyFilters}
                primaryActionLabel="Apply to message"
              />
            )}

            {/* Data Table */}
            <Datatable
              headers={tableHeaders}
              rows={tableRows}
              emptyRowsMessage={
                savedFiltersError 
                  ? savedFiltersError
                  : filteredData.length === 0 && savedFilters?.length > 0
                    ? `No recipient filters found matching "${activeSearchTerm}"`
                    : "No recipient recipient filters found"
              }
              data-testid={`${qaPrefix}-table`}
            />
            
            {/* Pagination */}
            {totalItems > 0 && !savedFiltersError && (
              <div className="saved-filters-pagination" data-testid={`${qaPrefix}-pagination`}>
                <Pagination
                  totalItems={totalItems}
                  itemsPerPageOptions={mode === ACTIONS.CAMPAIGN_PAGE ? [8, 16, 24] : [10, 20, 40]}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  itemOffset={itemOffset}
                  setItemOffset={setItemOffset}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
}

export default SavedFiltersDataTable;
